import EventEmitter from '@/lib/socket.io-plugin/emitter';
import Vue from 'vue';
import Vuex, { ActionContext } from 'vuex';
import modules from '@/store/index';
import { PlaceInit } from '@/interfaces/models/place';
import { Notice } from '@/interfaces/models/notice';
import globalApi from '@/api/global';

Vue.use(Vuex);

export interface RootState {
  isLoadingProps: boolean,
  isSocketConnected: boolean
  beforeInstallPromptEvent: Event | null;
  installApp: boolean;
}

export interface InitialData {
  placeList: Array<PlaceInit>;
  todayReservations: Array<any>;
  undoneReservations: Array<any>;
  todayTakeAways: Array<any>;
  undoneTakeAways: Array<any>;
  notificationList: Array<Notice>;
  sectionNotification: SectionNotification;
  utcNowInSeconds: number;
  lastEventId: number;
}

export type SectionNotification = {
  pos: {
    actualOrdersCount: number;
    savedOrdersCount: number;
  };
  kitchen: number;
  clients: number;
};

interface ClientActivity {
  id: string,
  count: number
}

const store = new Vuex.Store<RootState>({
  state: {
    isLoadingProps: true,
    isSocketConnected: false,
    beforeInstallPromptEvent: null,
    installApp: false,
  },
  mutations: {
    setBeforeInstallPromptEvent(state: RootState, beforeInstallPromptEvent: Event) {
      state.beforeInstallPromptEvent = beforeInstallPromptEvent;
    },
    socketConnect(state: RootState) {
      state.isSocketConnected = true;
    },
    socketDisconnect(state: RootState) {
      state.isSocketConnected = false;
    },
    setIsLoadingProps(state: RootState, value: boolean) {
      state.isLoadingProps = value;
    },
    installApp(state: RootState) {
      state.installApp = true;
    },
  },
  actions: {
    init({ commit, dispatch }: ActionContext<RootState, RootState>): Promise<any> {
      commit('setIsLoadingProps', true);

      return globalApi.init()
        .then((data: InitialData) => {
          dispatch('setSectionNotifications', data.sectionNotification);
          commit('notice/setNotices', data.notificationList);
          commit('place/setPlaces', data.placeList);
          commit('place/selectDefaultPlace');
          commit('eventLog/setLastEventId', data.lastEventId);
          dispatch('timer/startTimer', data.utcNowInSeconds);

          commit('setIsLoadingProps', false);
        });
    },
    fetchInitForNewPlace({ commit, dispatch }: ActionContext<RootState, RootState>, placeId: string): Promise<any> {
      commit('setIsLoadingProps', true);

      return globalApi.fetchInitForNewPlace(placeId)
        .then((data: any) => {
          dispatch('setSectionNotifications', data.sectionNotification);
          commit('eventLog/setLastEventId', data.lastEventId);
          commit('notice/setNotices', data.notificationList);
          dispatch('timer/startTimer', data.utcNowInSeconds);

          commit('setIsLoadingProps', false);
        });
    },
    setSectionNotifications({ commit, dispatch }: ActionContext<RootState, RootState>, sectionNotification: SectionNotification) {
      if (sectionNotification.pos) {
        commit('pos/setSectionNotification', sectionNotification.pos);
      }

      if (sectionNotification.kitchen) {
        commit('takeAway/setSectionNotification', sectionNotification.kitchen);
      }

      if (sectionNotification.clients) {
        commit('clients/setSectionNotification', sectionNotification.clients);
      }
    },
    async handleSocketEvents({ dispatch }: ActionContext<RootState, RootState>, events: { eventName: string, params: any }[]) {
      const eventEmitter = new EventEmitter({
        store,
        actionPrefix: '',
        mutationPrefix: ''
      });

      for (let index = 0; index < events.length; index++) {
        eventEmitter.dispatchStore(events[index].eventName, events[index].params);
      }
    },
    resetStore({ commit }: ActionContext<RootState, RootState>) {
      commit('pos/resetState');
      commit('menu/resetState');
      commit('notice/resetState');
      commit('takeAway/resetState');
      commit('storehouse/resetState');
      commit('settings/staff/resetState');
      commit('settings/device/resetState');
      commit('settings/promotion/resetState');
    },
    forceResetStore({ commit }: ActionContext<RootState, RootState>) {
      commit('user/removeUser');
      commit('place/resetState');
      commit('pos/resetState');
      commit('menu/resetState');
      commit('notice/resetState');
      commit('takeAway/resetState');
      commit('storehouse/resetState');
      commit('settings/staff/resetState');
      commit('settings/device/resetState');
      commit('settings/promotion/resetState');
      commit('dashboard/resetState');
      commit('clients/resetState');
    }
  },
  modules,
  strict: process.env.NODE_ENV !== 'production'
});

export default store;
